// Support console (operators / superadmin). Lists the grants customers have
// opened and renders exactly what each grant covers — read-only, through
// /support/* which refuses anything outside an active grant and logs every
// view into the customer's Operator access list. Nothing here reads
// Firestore directly: the backend is the gate.
import React, { useEffect, useState } from 'react';
import { useSuperadmin } from '../data/permissions';
import { GRANT_LEVEL_LABEL, supportListGrants, supportOpenGrant, supportOpenDoc, supportOpenSession } from '../data/supportAccess';

const fmtWhen = (at) => (at ? new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const scope = (g) => (g.level === 'org' ? 'Whole organization' : `${GRANT_LEVEL_LABEL[g.level] || g.level}: ${g.label || g.docId || g.dealId || g.sessionId}`);

function DocPane({ orgId, grantId, dealId, docId, title }) {
  const [state, setState] = useState({ data: null, error: '' });
  useEffect(() => {
    let alive = true;
    setState({ data: null, error: '' });
    supportOpenDoc(orgId, grantId, dealId, docId)
      .then((d) => alive && setState({ data: d, error: '' }))
      .catch((e) => alive && setState({ data: null, error: e.message }));
    return () => { alive = false; };
  }, [orgId, grantId, dealId, docId]);
  const { data, error } = state;
  if (error) return <div className="cs-empty">{error}</div>;
  if (!data) return <div className="cs-skeleton" style={{ height: 120 }} />;
  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-split">
        <strong>{title}</strong>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {data.latest && <span className="cs-badge">v{data.latest.number}</span>}
          {data.download && <a className="cs-btn cs-btn-outline cs-btn-sm" href={data.download} target="_blank" rel="noreferrer">Original file (10 min link)</a>}
        </span>
      </div>
      {data.versions?.length > 0 && (
        <span className="u-xs u-muted">
          {data.versions.length} version{data.versions.length === 1 ? '' : 's'} · latest by {data.versions[0].authorName || '—'} {fmtWhen(data.versions[0].createdAt)}
        </span>
      )}
      {data.latest?.clauses?.length ? (
        <div className="u-stack" style={{ '--gap': 'var(--sp-2)', maxHeight: 520, overflow: 'auto' }}>
          {data.latest.clauses.map((c, i) => (
            <div key={c.id || i} className="u-small">
              {c.heading && <strong>{c.heading}</strong>}
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
            </div>
          ))}
        </div>
      ) : data.pastedText ? (
        <div className="u-small" style={{ whiteSpace: 'pre-wrap', maxHeight: 520, overflow: 'auto' }}>{data.pastedText}</div>
      ) : (
        <span className="u-small u-muted">No text version yet{data.download ? ' — use the original file link.' : '.'}</span>
      )}
    </div>
  );
}

function SessionPane({ orgId, grantId, sessionId }) {
  const [state, setState] = useState({ data: null, error: '' });
  useEffect(() => {
    let alive = true;
    supportOpenSession(orgId, grantId, sessionId)
      .then((d) => alive && setState({ data: d, error: '' }))
      .catch((e) => alive && setState({ data: null, error: e.message }));
    return () => { alive = false; };
  }, [orgId, grantId, sessionId]);
  const { data, error } = state;
  if (error) return <div className="cs-empty">{error}</div>;
  if (!data) return <div className="cs-skeleton" style={{ height: 120 }} />;
  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      <strong>{data.session.title || 'Conversation'}</strong>
      {data.messages.map((m) => (
        <div key={m.id} className="u-small" style={{ padding: 'var(--sp-2) var(--sp-3)', background: m.role === 'user' ? 'var(--bg-elev)' : 'transparent', borderRadius: 'var(--r-lg)' }}>
          <span className="u-xs u-muted">{m.role} · {fmtWhen(m.createdAt)}</span>
          <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
        </div>
      ))}
    </div>
  );
}

function GrantView({ grant, onBack }) {
  const [state, setState] = useState({ data: null, error: '' });
  const [openDoc, setOpenDoc] = useState(null);
  useEffect(() => {
    let alive = true;
    supportOpenGrant(grant.orgId, grant.id)
      .then((d) => alive && setState({ data: d, error: '' }))
      .catch((e) => alive && setState({ data: null, error: e.message }));
    return () => { alive = false; };
  }, [grant.orgId, grant.id]);
  const { data, error } = state;
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-split">
        <div className="u-stack" style={{ '--gap': '2px' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>{grant.orgName} — {scope(grant)}</h2>
          <span className="u-xs u-muted">
            granted by {grant.grantedByName || grant.grantedByEmail} {fmtWhen(grant.createdAt)} · {grant.expiresAt ? `expires ${fmtWhen(grant.expiresAt)}` : 'until revoked'} · "{grant.reason}"
          </span>
        </div>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onBack}>All grants</button>
      </div>
      {error && <div className="cs-empty">{error}</div>}
      {!data && !error && <div className="cs-skeleton" style={{ height: 80 }} />}
      {data && grant.level === 'conversation' && <SessionPane orgId={grant.orgId} grantId={grant.id} sessionId={grant.sessionId} />}
      {data && grant.level !== 'conversation' && data.deals.map((d) => (
        <div key={d.id} className="cs-card u-stack" style={{ padding: 'var(--sp-3) var(--sp-4)', '--gap': 'var(--sp-2)' }}>
          <div className="u-split">
            <strong>{d.name}</strong>
            <span className="u-xs u-muted">{d.status || ''}{d.restricted ? ' · restricted' : ''}</span>
          </div>
          {!d.docs.length && <span className="u-small u-muted">No documents.</span>}
          {d.docs.map((doc) => (
            <button key={doc.id} type="button" className="cs-btn cs-btn-ghost cs-btn-sm" style={{ justifyContent: 'space-between', width: '100%' }} onClick={() => setOpenDoc({ dealId: d.id, docId: doc.id, title: doc.title })}>
              <span>{doc.title}</span>
              <span className="u-xs u-muted">{doc.docType || doc.kind}{doc.hasFile ? ' · file' : ''}</span>
            </button>
          ))}
        </div>
      ))}
      {openDoc && <DocPane orgId={grant.orgId} grantId={grant.id} {...openDoc} />}
    </div>
  );
}

export default function Support() {
  const superadmin = useSuperadmin();
  const [grants, setGrants] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!superadmin) return;
    supportListGrants().then(setGrants).catch((e) => { setError(e.message); setGrants([]); });
  }, [superadmin]);

  if (!superadmin) return <div className="cs-page"><p className="cs-help">The support console is for operators (superadmin claim).</p></div>;

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-6)' }}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <h1 style={{ margin: 0 }}>Support console</h1>
        <span className="u-muted u-small">
          Customer content opens here only under a grant the customer's admins created, read-only, and every view lands in their Operator access log. No grant, no access.
        </span>
      </div>
      {open ? (
        <GrantView grant={open} onBack={() => setOpen(null)} />
      ) : (
        <section className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>Active grants</h2>
          {error && <div className="cs-empty">{error}</div>}
          {grants === null && !error && <div className="cs-skeleton" style={{ height: 80 }} />}
          {grants !== null && !grants.length && <div className="cs-empty">No customer has granted support access right now.</div>}
          {(grants || []).map((g) => (
            <button key={`${g.orgId}/${g.id}`} type="button" className="cs-card u-split" style={{ padding: 'var(--sp-3) var(--sp-4)', textAlign: 'left', cursor: 'pointer', width: '100%' }} onClick={() => setOpen(g)}>
              <span className="u-stack" style={{ '--gap': '2px', flex: 1, minWidth: 0 }}>
                <span className="u-small" style={{ fontWeight: 700 }}>{g.orgName} — {scope(g)}</span>
                <span className="u-xs u-muted">by {g.grantedByName || g.grantedByEmail} · {fmtWhen(g.createdAt)} · {g.expiresAt ? `expires ${fmtWhen(g.expiresAt)}` : 'until revoked'} · "{g.reason}"</span>
              </span>
              <span className="cs-badge b-info">open</span>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
